/* =========================================================
   Affiche VTT & GRAVEL — le vélo, le profil, la sortie, la saison, la course.
   API : SNVtVelo.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, lieu, joueur, equipe,
              mode 'velo'    → type (vtt|gravel), roue, cadre, debattement,
                               pneus, transmission, l1..l6
              mode 'profil'  → p1..p8 (« 12,4|860 ») : km parcourus, altitude
              mode 'sortie'  → t1..t6 (« Valeur|Libellé »)
              mode 'saison'  → l1..l8 (« Libellé|valeur »)
              mode 'course'  → temps, place, c1..c6 (« Spéciale 1|4:12 ») }
     opts : { palette }

   LE VÉLO EST DESSINÉ, PAS ILLUSTRÉ AU HASARD. Le type choisi pilote la
   géométrie : un VTT reçoit un cintre plat et une fourche télescopique, un
   gravel un cintre route et une fourche rigide. Ce sont les deux seules choses
   qu'on distingue à coup sûr sur une silhouette de vélo, donc ce sont celles
   qui changent.

   Auto-suffisant (aucun fetch, aucune image, aucun aléa) → re-render serveur.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    terre:   { label:'Terre',   bg:'#2A2118', ink:'#F5EFE4', muted:'#B49B7E', accent:'#E8A33D', line:'#463628', card:'#382B1F' },
    craie:   { label:'Craie',   bg:'#F4F1EA', ink:'#22262B', muted:'#8A8D93', accent:'#C2410C', line:'#E1DCD1', card:'#FFFFFF' },
    foret:   { label:'Forêt',   bg:'#16281F', ink:'#F1F0E6', muted:'#8FA894', accent:'#E8B33D', line:'#2A4034', card:'#1E3428' },
    nuit:    { label:'Nuit',    bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').replace(/\s/g, '').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fr = n => String(n).replace('.', ',');
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };
  const cleFermee = (v, table, defaut) => {
    const k = String(v == null ? '' : v).trim().toLowerCase().replace(/[\s-]/g, '');
    return Object.prototype.hasOwnProperty.call(table, k) ? k : defaut;
  };
  const TYPES = { vtt: 'VTT', gravel: 'Gravel' };

  /* ---------------------------------------------------------------
     LE VÉLO, de profil. Roues, triangles avant et arrière, fourche, cintre,
     selle, pédalier. Deux différences pilotées par le type, et ce sont les
     seules qu'on distingue à coup sûr sur une silhouette :
       VTT    → cintre plat, fourche télescopique (deux plongeurs)
       gravel → cintre route en boucle, fourche rigide effilée
     --------------------------------------------------------------- */
  function velo(cx, cy, ecart, type, p) {
    const r = ecart * 0.335;                       /* rayon de roue */
    const xAr = cx - ecart / 2, xAv = cx + ecart / 2;
    const t = Math.max(3, ecart * 0.017);          /* épaisseur des tubes */
    let g = '';

    /* Les roues : pneu épais, jante fine, moyeu. */
    for (const x of [xAr, xAv]) {
      g += `<circle cx="${x.toFixed(1)}" cy="${cy}" r="${r.toFixed(1)}" fill="none" stroke="${p.ink}" stroke-width="${(t * 1.9).toFixed(1)}"/>`
        + `<circle cx="${x.toFixed(1)}" cy="${cy}" r="${(r * 0.80).toFixed(1)}" fill="none" stroke="${p.muted}" stroke-width="${(t * 0.6).toFixed(1)}"/>`
        + `<circle cx="${x.toFixed(1)}" cy="${cy}" r="${(t * 1.4).toFixed(1)}" fill="${p.ink}"/>`;
      /* quelques rayons, symétriques donc déterministes */
      for (let a = 0; a < 8; a++) {
        const th = (a * 45 + 12) * Math.PI / 180;
        g += `<line x1="${(x + t * 1.2 * Math.cos(th)).toFixed(1)}" y1="${(cy + t * 1.2 * Math.sin(th)).toFixed(1)}"`
          + ` x2="${(x + r * 0.80 * Math.cos(th)).toFixed(1)}" y2="${(cy + r * 0.80 * Math.sin(th)).toFixed(1)}"`
          + ` stroke="${p.muted}" stroke-width="${(t * 0.28).toFixed(1)}" opacity="0.7"/>`;
      }
    }

    /* Les points du cadre, nommés une fois. */
    const bb = [cx - ecart * 0.10, cy + r * 0.06];              /* boîtier de pédalier */
    const stH = [cx - ecart * 0.19, cy - r * 0.86];             /* haut du tube de selle */
    const htH = [cx + ecart * 0.28, cy - r * 0.80];             /* haut du tube de direction */
    const htB = [cx + ecart * 0.33, cy - r * 0.40];             /* bas du tube de direction */
    const tube = (a, b, ep) => `<line x1="${a[0].toFixed(1)}" y1="${a[1].toFixed(1)}" x2="${b[0].toFixed(1)}" y2="${b[1].toFixed(1)}" stroke="${p.accent}" stroke-width="${(ep || t).toFixed(1)}" stroke-linecap="round"/>`;
    g += tube(bb, stH) + tube(bb, htB) + tube(stH, htH)          /* selle, diagonal, supérieur */
      + tube(bb, [xAr, cy]) + tube(stH, [xAr, cy])               /* bases et haubans */
      + tube(htH, htB, t * 1.5);                                 /* tube de direction */

    /* La fourche : télescopique en VTT, rigide et effilée en gravel. */
    if (type === 'vtt') {
      g += `<line x1="${htB[0].toFixed(1)}" y1="${htB[1].toFixed(1)}" x2="${(xAv - t * 0.6).toFixed(1)}" y2="${cy}" stroke="${p.ink}" stroke-width="${(t * 2.1).toFixed(1)}" stroke-linecap="round"/>`
        + `<line x1="${((htB[0] + xAv) / 2).toFixed(1)}" y1="${((htB[1] + cy) / 2).toFixed(1)}" x2="${(xAv - t * 0.6).toFixed(1)}" y2="${cy}" stroke="${p.muted}" stroke-width="${(t * 1.2).toFixed(1)}" stroke-linecap="round"/>`;
    } else {
      g += `<path d="M ${htB[0].toFixed(1)} ${htB[1].toFixed(1)} Q ${(htB[0] + ecart * 0.05).toFixed(1)} ${((htB[1] + cy) / 2).toFixed(1)} ${xAv.toFixed(1)} ${cy}"`
        + ` fill="none" stroke="${p.ink}" stroke-width="${(t * 1.3).toFixed(1)}" stroke-linecap="round"/>`;
    }

    /* Le cintre : plat en VTT, en boucle sur un gravel. */
    const cbx = htH[0] + ecart * 0.015, cby = htH[1] - r * 0.16;
    g += tube(htH, [cbx, cby], t * 1.1);
    if (type === 'vtt')
      g += `<line x1="${(cbx - ecart * 0.055).toFixed(1)}" y1="${cby.toFixed(1)}" x2="${(cbx + ecart * 0.035).toFixed(1)}" y2="${(cby - r * 0.05).toFixed(1)}" stroke="${p.ink}" stroke-width="${(t * 1.3).toFixed(1)}" stroke-linecap="round"/>`;
    else
      g += `<path d="M ${(cbx - ecart * 0.035).toFixed(1)} ${cby.toFixed(1)} L ${(cbx + ecart * 0.020).toFixed(1)} ${(cby - r * 0.03).toFixed(1)}`
        + ` Q ${(cbx + ecart * 0.062).toFixed(1)} ${(cby - r * 0.02).toFixed(1)} ${(cbx + ecart * 0.058).toFixed(1)} ${(cby + r * 0.13).toFixed(1)}`
        + ` Q ${(cbx + ecart * 0.052).toFixed(1)} ${(cby + r * 0.27).toFixed(1)} ${(cbx + ecart * 0.020).toFixed(1)} ${(cby + r * 0.28).toFixed(1)}"`
        + ` fill="none" stroke="${p.ink}" stroke-width="${(t * 1.3).toFixed(1)}" stroke-linecap="round" stroke-linejoin="round"/>`;

    /* La selle, en bec. */
    g += `<path d="M ${(stH[0] - ecart * 0.048).toFixed(1)} ${(stH[1] - r * 0.10).toFixed(1)}`
      + ` Q ${(stH[0] - ecart * 0.010).toFixed(1)} ${(stH[1] - r * 0.19).toFixed(1)} ${(stH[0] + ecart * 0.042).toFixed(1)} ${(stH[1] - r * 0.12).toFixed(1)}`
      + ` Q ${(stH[0] + ecart * 0.005).toFixed(1)} ${(stH[1] - r * 0.05).toFixed(1)} ${(stH[0] - ecart * 0.048).toFixed(1)} ${(stH[1] - r * 0.10).toFixed(1)} Z" fill="${p.ink}"/>`
      + tube(stH, [stH[0], stH[1] - r * 0.13], t * 0.9);

    /* Le pédalier et sa manivelle. */
    g += `<circle cx="${bb[0].toFixed(1)}" cy="${bb[1].toFixed(1)}" r="${(r * 0.21).toFixed(1)}" fill="none" stroke="${p.ink}" stroke-width="${(t * 0.9).toFixed(1)}"/>`
      + `<line x1="${bb[0].toFixed(1)}" y1="${bb[1].toFixed(1)}" x2="${(bb[0] - r * 0.10).toFixed(1)}" y2="${(bb[1] + r * 0.28).toFixed(1)}" stroke="${p.ink}" stroke-width="${(t * 1.1).toFixed(1)}" stroke-linecap="round"/>`
      /* la chaîne, du plateau à la roue libre */
      + `<line x1="${(bb[0] + r * 0.21).toFixed(1)}" y1="${(bb[1] - r * 0.04).toFixed(1)}" x2="${(xAr + r * 0.10).toFixed(1)}" y2="${(cy - r * 0.05).toFixed(1)}" stroke="${p.muted}" stroke-width="${(t * 0.55).toFixed(1)}"/>`
      + `<line x1="${(bb[0] + r * 0.18).toFixed(1)}" y1="${(bb[1] + r * 0.14).toFixed(1)}" x2="${(xAr + r * 0.08).toFixed(1)}" y2="${(cy + r * 0.11).toFixed(1)}" stroke="${p.muted}" stroke-width="${(t * 0.55).toFixed(1)}"/>`;
    return g;
  }

  function stats(p, items, y) {
    const it = (items || []).filter(x => x && String(x[0] || '').trim());
    if (!it.length) return '';
    const x0 = 54, w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 36, 0.56, 0, 15), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }
  function troisValeurs(p, data, y, defaut) {
    const perso = [1, 2, 3].map(i => {
      const raw = String(data['s' + i] || '').trim(); if (!raw) return null;
      const [v, l] = raw.split('|').map(s => (s || '').trim());
      return v ? [v, l || ''] : null;
    });
    return stats(p, perso.some(Boolean) ? perso.filter(Boolean) : (defaut || []), y);
  }
  function lignes(data, cle, n) {
    const l = [];
    for (let i = 1; i <= n; i++) {
      const raw = String(data[cle + i] || '').trim(); if (!raw) continue;
      const [a, b] = raw.split('|').map(x => (x || '').trim());
      if (a && b) l.push([a, b]);
    }
    return l;
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.terre;
    const mode = String(data.mode || 'velo').toLowerCase();
    const type = cleFermee(data.type, TYPES, 'vtt');
    const cx = PW / 2;

    const DEF = { velo: 'MON VÉLO', profil: 'LE PROFIL', sortie: 'LA SORTIE', saison: 'MA SAISON', course: 'LA COURSE' };
    const titre = up(data.titre) || DEF[mode] || 'MON VÉLO';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || up(data.club) || up(TYPES[type]);
    const sous = [up(data.club), up(data.lieu)].filter(Boolean).join(' · ');

    let corps = '', barre = '', pied = 0;

    if (mode === 'velo') {
      /* Empattement borné : à 470, roues comprises, le vélo mesurait 784 de
         large et mordait sur les 10,4 unités que le massicot emporte de chaque
         côté. 400 donne 668 de large, soit 66 unités de marge. */
      corps = velo(cx, 452, 400, type, p)
        + T(cx, 622, 11.5, p.muted, `${up(TYPES[type])} · ${type === 'vtt' ? 'CINTRE PLAT ET FOURCHE TÉLESCOPIQUE' : 'CINTRE ROUTE ET FOURCHE RIGIDE'}`, { w:600, ls:2.6 });
      const l = lignes(data, 'l', 6);
      if (l.length) {
        const y0 = 686, pas = Math.min(40, 200 / Math.max(1, l.length - 1 || 1));
        l.forEach((r, i) => {
          const y = y0 + i * pas;
          corps += T(MX, y, 13, p.muted, esc(up(r[0])), { w:600, ls:2.2, a:'start' })
            + T(PW - MX, y + 4, fit(r[1], 400, 24, 0.58, 0, 13), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 5).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 5).toFixed(1)}" stroke="${p.line}" stroke-width="1.2"/>`;
        });
      }
      barre = troisValeurs(p, data, 966, [[esc(String(data.roue || '').trim()) || '—', 'Roues'],
        [esc(String(data.cadre || '').trim()) || '—', 'Cadre'],
        [esc(String(data.debattement || '').trim()) || '—', type === 'vtt' ? 'Débattement' : 'Pneus']]);
      pied = 1032;

    } else if (mode === 'profil') {
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['p' + i] || '').trim(); if (!raw) continue;
        const [a, b] = raw.split('|').map(x => (x || '').trim());
        const km = num(a), alt = num(b);
        if (km != null && alt != null && km >= 0) l.push({ km, alt });
      }
      l.sort((a, b) => a.km - b.km);
      if (l.length >= 2) {
        const X0 = MX + 52, X1 = PW - MX - 10, Y0 = 330, Y1 = 830;
        const kmMax = Math.max.apply(null, l.map(r => r.km)) || 1;
        const aMin = Math.min.apply(null, l.map(r => r.alt)), aMax = Math.max.apply(null, l.map(r => r.alt));
        const pas = (aMax - aMin) <= 300 ? 100 : (aMax - aMin) <= 800 ? 200 : 500;
        const bas = Math.floor(aMin / pas) * pas, haut = Math.ceil(aMax / pas) * pas;
        const px = km => X0 + (X1 - X0) * (km / kmMax);
        const py = a => Y1 - (Y1 - Y0) * (a - bas) / Math.max(1, haut - bas);
        for (let a = bas; a <= haut + 0.001; a += pas)
          corps += `<line x1="${X0}" y1="${py(a).toFixed(1)}" x2="${X1}" y2="${py(a).toFixed(1)}" stroke="${p.line}" stroke-width="1.2"/>`
            + T(X0 - 12, py(a) + 5, 12, p.muted, `${a} M`, { w:600, ls:1.2, a:'end' });
        corps += `<path d="M ${px(l[0].km).toFixed(1)} ${Y1.toFixed(1)} `
          + l.map(r => `L ${px(r.km).toFixed(1)} ${py(r.alt).toFixed(1)}`).join(' ')
          + ` L ${px(l[l.length-1].km).toFixed(1)} ${Y1.toFixed(1)} Z" fill="${p.card}" opacity="0.92"/>`
          + `<polyline points="${l.map(r => `${px(r.km).toFixed(1)},${py(r.alt).toFixed(1)}`).join(' ')}" fill="none" stroke="${p.accent}" stroke-width="3.5" stroke-linejoin="round" stroke-linecap="round"/>`;
        for (const r of l) corps += `<circle cx="${px(r.km).toFixed(1)}" cy="${py(r.alt).toFixed(1)}" r="4.5" fill="${p.bg}" stroke="${p.accent}" stroke-width="2.5"/>`;
        /* Le SOMMET, nommé : c'est le point qu'on regarde sur un profil. */
        const haut2 = l.reduce((a, b) => b.alt > a.alt ? b : a);
        corps += `<circle cx="${px(haut2.km).toFixed(1)}" cy="${py(haut2.alt).toFixed(1)}" r="8" fill="${p.accent}"/>`
          + T(px(haut2.km), py(haut2.alt) - 18, 18, p.ink, `${haut2.alt} M`, { f:'Oswald', w:700 })
          + T((X0 + X1) / 2, Y1 + 44, 11.5, p.muted, `${fr(kmMax)} KM · RELEVÉS DE LA TRACE`, { w:600, ls:2.6 });
        /* Le dénivelé positif est la SOMME DES MONTÉES, pas l'écart entre le
           point le plus bas et le plus haut : c'est toute la différence entre
           un col et une sortie vallonnée. */
        let dPlus = 0;
        for (let i = 1; i < l.length; i++) if (l[i].alt > l[i-1].alt) dPlus += l[i].alt - l[i-1].alt;
        barre = stats(p, [[`${Math.round(dPlus)} m`, 'Dénivelé positif'], [`${fr(kmMax)} km`, 'Distance'], [`${aMax} m`, 'Point haut']], 962);
      } else corps = T(cx, 600, 19, p.muted, 'DEUX POINTS SUFFISENT : LE KILOMÈTRE, PUIS L’ALTITUDE', { w:600, ls:1.4 });
      pied = 1030;

    } else if (mode === 'sortie') {
      const bl = lignes(data, 't', 6);
      if (bl.length) {
        const cols = bl.length <= 4 ? 2 : 3, rangs = Math.ceil(bl.length / cols);
        const bw = (PW - 2*MX - (cols-1)*16) / cols, bh = Math.min(180, 560 / rangs);
        bl.forEach((b, i) => {
          const c = i % cols, r = Math.floor(i / cols);
          const x = MX + c * (bw + 16), y = 340 + r * (bh + 16);
          corps += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${bh.toFixed(1)}" rx="16" fill="${p.card}"/>`
            + T(x + bw/2, y + bh*0.60, fit(b[0], bw - 28, 58, 0.56, 0, 22), p.ink, esc(b[0]), { f:'Oswald', w:700 })
            + T(x + bw/2, y + bh*0.82, 13, p.muted, esc(up(b[1])), { w:600, ls:2 });
        });
      } else corps = T(cx, 600, 19, p.muted, 'FORMAT : LA VALEUR, UNE BARRE VERTICALE, SON LIBELLÉ', { w:600, ls:1.5 });
      barre = troisValeurs(p, data, 976, null);
      pied = 1032;

    } else if (mode === 'course') {
      corps = `<rect x="${MX}" y="268" width="${PW-2*MX}" height="128" rx="14" fill="${p.ink}"/>`
        + T(cx, 308, 13, p.accent, esc(up(data.place)) || up(TYPES[type]), { w:700, ls:6 })
        + T(cx, 368, fit(esc(up(data.temps)) || '—', 560, 58, 0.56, 0, 26), p.bg, esc(up(data.temps)) || '—', { f:'Oswald', w:700 });
      const c = lignes(data, 'c', 6);
      if (c.length) {
        const y0 = 470, pas = Math.min(84, 420 / Math.max(1, c.length - 1 || 1));
        c.forEach((r, i) => {
          const y = y0 + i * pas;
          corps += T(MX, y, 14, p.muted, esc(up(r[0])), { w:600, ls:2.2, a:'start' })
            + T(PW - MX, y + 6, fit(r[1], 320, 30, 0.58, 0, 15), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (i < c.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 6).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 6).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps += T(cx, 620, 19, p.muted, 'FORMAT : LE NOM DE LA SPÉCIALE, PUIS LE TEMPS', { w:600, ls:1.5 });
      barre = troisValeurs(p, data, 962, null);
      pied = 1030;

    } else { /* saison */
      const l = lignes(data, 'l', 8);
      if (l.length) {
        const zone = 600, pas = Math.min(96, zone / l.length), y0 = 308 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas, dernier = i === l.length - 1;
          corps += T(MX, y, 17, p.muted, esc(up(r[0])), { w:600, ls:2, a:'start' })
            + T(PW - MX, y + 8, fit(r[1], 420, 44, 0.58, 0, 20), dernier ? p.accent : p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (!dernier) corps += `<line x1="${MX}" y1="${(y + pas/2 - 14).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 14).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LIBELLÉ, BARRE VERTICALE, VALEUR', { w:600, ls:2 });
      pied = 976;
    }

    const bas = [esc(up(data.joueur)), esc(up(data.equipe))].filter(Boolean).join(' · ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${barre}
${pied && bas ? T(cx, pied, fit(bas, 690, 19, 0.60, 1, 12), p.ink, bas, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNVtVelo = { render, PALETTES, TYPES };
})();
